import { getErrorMessage } from '@smartsash/api-client';
import {
  hashKey,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';

import type { EntityName } from './entities';
import { invalidations, keyInAnyTree, type MutationCtx } from './invalidations';
import { recordTreesOf } from './keys';
import { emitNotification } from './notify';

// Mutation factories (03-03 §2.3). Apps NEVER call generated mutation hooks
// directly (lint-enforced, 03-02 §6) — they compose them here so invalidation,
// notification and error normalization stay uniform across every entity.

/** Default success toast keys per CRUD kind; apps translate these. */
export const SUCCESS_KEYS = {
  create: 'serverOps.toast.created',
  update: 'serverOps.toast.updated',
  delete: 'serverOps.toast.deleted',
} as const;

/**
 * Orval's `getXxxMutationOptions` generator: returns a ready `UseMutationOptions`
 * carrying the `mutationFn` + `mutationKey`. We only ever call it with no args (the
 * per-call `request`/`mutation` overrides orval accepts are typed per-endpoint and
 * are not part of this contract), so the parameter is `never` — that keeps the real
 * generated factories (whose option shapes differ per endpoint) assignable here,
 * while a hand-written `() => ({ mutationFn })` stub still satisfies it.
 */
export type MutationOptionsFactory<TData, TError, TVars, TContext> = (
  options?: never,
) => UseMutationOptions<TData, TError, TVars, TContext>;

/**
 * Opt-in optimistic recipe. `apply` mutates the cache and returns a rollback
 * thunk invoked on error. The generic list insert/patch/remove engine is
 * deferred until data-screen pins the list-cache shape — until then callers pass
 * an entity-specific recipe here. See [[jira-progress-tracking]].
 */
export interface OptimisticRecipe<TVars> {
  apply(qc: ReturnType<typeof useQueryClient>, vars: TVars): Promise<() => void> | (() => void);
}

export interface EntityMutationConfig<TData, TError, TVars> {
  entity: EntityName;
  mutationOptions: MutationOptionsFactory<TData, TError, TVars, unknown>;
  /** Toast key on success; omit for silent success. */
  successMessageKey?: string;
  /**
   * Override the error toast's title key for this call. Return `undefined` to fall back to
   * the default `getErrorMessage(error)` mapping. HS-1375: some endpoints return a status
   * whose default title (e.g. `errors.validation` for any unlisted 4xx) reads as generic or
   * misleading for that specific call; this lets one call site say something more useful
   * without changing what every other caller of the same entity sees. The toast's
   * `detail` — the server's raw text, when it sent any — is untouched either way.
   */
  errorTitleKey?: (error: TError) => string | undefined;
  /**
   * `false` skips the error toast for this call (default `true`). For a caller that renders the
   * failure itself — the HS-1611 import wizard shows a refused import on its own step and a lost
   * one as a result banner — so the admin is not told the same thing twice. Invalidation, rollback
   * and the caller's own `onError` still run.
   */
  notifyError?: boolean;
  /**
   * The call's vars name the record a successful call removes — `id`, or `institutionId` plus
   * `labId` / `userId` (HS-1141). Its key trees (`recordTreesOf`) are cancelled and left out of the
   * invalidation, so a page still showing the record does not refetch a read that can only 404;
   * cached reads of it that nothing shows any more are dropped. Ignored on error: the record is
   * still there. With `settleInvalidation: 'lastInFlight'` only the invalidating call's record is
   * excluded; an earlier call's record, if still shown, is refetched (no delete combines them yet).
   * Set it through `useEntityDelete`, and only where the vars ARE the removed record:
   * a logo delete also carries the institution's `id`, and must not drop the institution's reads.
   * (Typed as a plain flag: a field that mentions `TVars` stops TypeScript inferring the vars from
   * the generated mutation options.)
   */
  deletesRecord?: boolean;
  /** Derive the invalidation context (reserved for id-scoped precision). */
  toCtx?: (vars: TVars, data: TData | undefined) => MutationCtx;
  optimistic?: OptimisticRecipe<TVars>;
  /**
   * When the entity's invalidation runs on settle. `'each'` (default): after every call.
   * `'lastInFlight'`: only when no other `'lastInFlight'` call with the same mutation key is still
   * running on the same `QueryClient` (a counter, not `isMutating()`, see `inFlightByClient`), so a
   * refetch started by an earlier call cannot overwrite the optimistic write of a later one
   * still on its way (HS-1542, the dashboard's alert assignment). Needs a generated
   * `mutationKey`; without one it behaves as `'each'`.
   */
  settleInvalidation?: 'each' | 'lastInFlight';
  /** App-level RQ overrides; they compose AFTER the factory's own handlers. */
  mutation?: Partial<UseMutationOptions<TData, TError, TVars, MutationRollbackContext>>;
}

/** What `onMutate` stashes for later handlers: the optimistic rollback thunk. */
export interface MutationRollbackContext {
  rollback?: () => void;
}

/**
 * Calls in flight per client and hashed mutation key, for `settleInvalidation: 'lastInFlight'`.
 * `isMutating()` cannot answer "am I the last one": when two calls settle in the same task, each
 * `onSettled` still sees the other as pending and both skip the invalidation. This counter is
 * incremented in `onMutate` and decremented synchronously at the start of `onSettled`, so exactly
 * one call observes zero.
 */
const inFlightByClient = new WeakMap<QueryClient, Map<string, number>>();

function inFlightCounts(qc: QueryClient): Map<string, number> {
  let counts = inFlightByClient.get(qc);
  if (!counts) {
    counts = new Map();
    inFlightByClient.set(qc, counts);
  }
  return counts;
}

/** Core factory: generated mutation + invalidation + notify + normalized errors. */
export function useEntityMutation<TData, TError, TVars>(
  config: EntityMutationConfig<TData, TError, TVars>,
): UseMutationResult<TData, TError, TVars, MutationRollbackContext> {
  const qc = useQueryClient();
  const generated = config.mutationOptions();
  const invalidate = invalidations[config.entity];
  const overrides = config.mutation;
  const flightKey =
    config.settleInvalidation === 'lastInFlight' && generated.mutationKey !== undefined
      ? hashKey(generated.mutationKey)
      : undefined;

  return useMutation<TData, TError, TVars, MutationRollbackContext>(
    {
      // Take only the network binding from the generated options; the lifecycle
      // callbacks below are ours (its context type differs from ours anyway).
      mutationFn: generated.mutationFn,
      mutationKey: generated.mutationKey,
      onMutate: async (vars) => {
        // Counted before any await, so a call is in flight from the moment it starts.
        if (flightKey !== undefined) {
          const counts = inFlightCounts(qc);
          counts.set(flightKey, (counts.get(flightKey) ?? 0) + 1);
        }
        const rollback = await config.optimistic?.apply(qc, vars);
        return { rollback: rollback ?? undefined };
      },
      onError: (error, vars, onMutateResult, context) => {
        onMutateResult?.rollback?.();
        if (config.notifyError !== false) {
          emitNotification({
            level: 'error',
            messageKey: config.errorTitleKey?.(error) ?? getErrorMessage(error),
            detail: error instanceof Error ? error.message : undefined,
          });
        }
        overrides?.onError?.(error, vars, onMutateResult, context);
      },
      onSuccess: (data, vars, onMutateResult, context) => {
        if (config.successMessageKey) {
          emitNotification({ level: 'success', messageKey: config.successMessageKey });
        }
        overrides?.onSuccess?.(data, vars, onMutateResult, context);
      },
      onSettled: async (data, error, vars, onMutateResult, context) => {
        let lastInFlight = true;
        if (flightKey !== undefined) {
          const counts = inFlightCounts(qc);
          const remaining = Math.max((counts.get(flightKey) ?? 0) - 1, 0);
          if (remaining === 0) counts.delete(flightKey);
          else counts.set(flightKey, remaining);
          lastInFlight = remaining === 0;
        }
        const deleted =
          error === null && config.deletesRecord
            ? recordTreesOf(config.entity, vars as MutationCtx)
            : [];
        const inDeleted = (query: { queryKey: readonly unknown[] }) =>
          keyInAnyTree(query.queryKey, deleted);
        if (deleted.length > 0) await qc.cancelQueries({ predicate: inDeleted });
        if (lastInFlight) {
          const ctx = config.toCtx?.(vars, data);
          await invalidate(qc, deleted.length > 0 ? { ...ctx, exclude: deleted } : ctx);
        }
        // Inactive only: removing a query a mounted page still observes makes the observer rebuild
        // and fetch it again. An active one keeps its data until the page leaves, then is gc'd.
        if (deleted.length > 0) qc.removeQueries({ type: 'inactive', predicate: inDeleted });
        await overrides?.onSettled?.(data, error, vars, onMutateResult, context);
      },
    },
    qc,
  );
}

type NamedConfig<TData, TError, TVars> = Omit<
  EntityMutationConfig<TData, TError, TVars>,
  'successMessageKey'
> & { successMessageKey?: string };

export function useEntityCreate<TData, TError, TVars>(
  config: Omit<NamedConfig<TData, TError, TVars>, 'deletesRecord'>,
): UseMutationResult<TData, TError, TVars, MutationRollbackContext> {
  return useEntityMutation({ successMessageKey: SUCCESS_KEYS.create, ...config });
}

export function useEntityUpdate<TData, TError, TVars>(
  config: Omit<NamedConfig<TData, TError, TVars>, 'deletesRecord'>,
): UseMutationResult<TData, TError, TVars, MutationRollbackContext> {
  return useEntityMutation({ successMessageKey: SUCCESS_KEYS.update, ...config });
}

export function useEntityDelete<TData, TError, TVars>(
  config: NamedConfig<TData, TError, TVars>,
): UseMutationResult<TData, TError, TVars, MutationRollbackContext> {
  return useEntityMutation({ successMessageKey: SUCCESS_KEYS.delete, ...config });
}
